/**
 * ISPHSolver.ts
 *
 * SPH Solver Strategy Interface and Factory
 *
 * This file defines the contract for all SPH solver implementations (DFSPH, PBF, PCISPH).
 * Each solver provides its own GPU compute pipeline, buffers, and execution logic.
 *
 * Architecture:
 * - ISPHSolver: Interface that all solvers implement
 * - GPUSolverBuffers: Container for solver-specific GPU buffers
 * - SolverFabrika: Factory for creating solver instances
 * - Helper types for pipeline management
 *
 * @module fiko-sph-sivi/gpu
 */

import { SolverTipi } from '../FikoSPHParametreler';
import { FikoDFSPHSolver } from './FikoDFSPHSolver';
import { FikoPBFSolver } from './FikoPBFSolver';
import { FikoPCISPHSolver } from './FikoPCISPHSolver';

// ============================================================================
// GPU Buffer Management Types
// ============================================================================

/**
 * Container for solver-specific GPU buffers
 *
 * Each solver type requires different auxiliary buffers:
 * - DFSPH: alpha, predicted velocity
 * - PBF: lambda, delta position, predicted position
 * - PCISPH: pressure, pressure force, predicted position, predicted density
 */
export interface GPUSolverBuffers {
  /** Map of buffer name to GPU buffer */
  buffers: Map<string, GPUBuffer>;

  /** Total bytes allocated for all buffers */
  totalBytes: number;

  /** Clean up all GPU resources */
  dispose(): void;
}

/**
 * Bind group layout entry description
 * Simplifies bind group creation for solver-specific buffers
 */
export interface BindGroupGirisi {
  /** Binding index in shader (@binding(N)) */
  binding: number;

  /** GPU buffer reference */
  buffer: GPUBuffer;

  /** Optional offset into buffer (bytes) */
  offset?: number;

  /** Optional size to bind (bytes, defaults to entire buffer) */
  size?: number;
}

// ============================================================================
// SPH Solver Interface
// ============================================================================

/**
 * ISPHSolver - Strategy pattern interface for SPH solvers
 *
 * Each solver implementation provides:
 * 1. Initialization (shader compilation, pipeline creation)
 * 2. Buffer management (create/destroy solver-specific buffers)
 * 3. Compute pass encoding (the actual physics computation)
 * 4. Resource cleanup
 *
 * Execution flow per frame:
 * 1. guncelleParametreler() - update uniforms
 * 2. encodeSolverPasslari() - encode compute commands
 * 3. (Optional) integration step if kendiIntegrasyonuVar = false
 */
export interface ISPHSolver {
  /** Solver name identifier */
  readonly ad: string;  // 'DFSPH' | 'PBF' | 'PCISPH'

  /** Number of GPU compute passes this solver requires */
  readonly passSayisi: number;

  /**
   * Initialize solver resources
   *
   * Creates shader modules, compute pipelines, and bind group layouts.
   * Called once when solver is selected or when switching solvers.
   *
   * @param device - WebGPU device
   * @param maxParticle - Maximum particle count for buffer sizing
   * @param ortakBindGroupLayout - Shared bind group layout (uniforms + particles + grid)
   */
  basla(
    device: GPUDevice,
    maxParticle: number,
    ortakBindGroupLayout: GPUBindGroupLayout,
  ): Promise<void>;

  /**
   * Create solver-specific GPU buffers
   *
   * Called after basla() and when maxParticle count changes.
   * Each solver allocates its own auxiliary data buffers.
   *
   * @param device - WebGPU device
   * @param maxParticle - Number of particles to allocate for
   * @returns Container with all solver buffers
   */
  olusturBufferlar(device: GPUDevice, maxParticle: number): GPUSolverBuffers;

  /**
   * Get solver-specific bind group layout
   *
   * Returns the layout for @group(3) in shaders.
   * This layout describes solver-specific buffers.
   *
   * @returns Bind group layout for solver buffers
   */
  solverBindGroupLayout(): GPUBindGroupLayout;

  /**
   * Create solver-specific bind group
   *
   * Binds solver buffers to @group(3) according to solverBindGroupLayout.
   *
   * @param device - WebGPU device
   * @param buffers - Solver-specific buffers to bind
   * @returns Configured bind group
   */
  olusturBindGroup(device: GPUDevice, buffers: GPUSolverBuffers): GPUBindGroup;

  /**
   * Encode solver-specific compute passes
   *
   * This is the core solver execution. Encodes all compute passes needed
   * to solve the SPH equations (pressure, viscosity, etc.)
   *
   * Called between neighbor search and integration phases.
   *
   * @param encoder - GPU command encoder
   * @param ortakBindGroup - Shared uniforms (@group(0))
   * @param particleBindGroup - Particle SoA buffers (@group(1))
   * @param gridBindGroup - Grid/neighbor buffers (@group(2))
   * @param solverBindGroup - Solver-specific buffers (@group(3))
   * @param workgroupCount - Number of workgroups to dispatch (ceil(activeParticles / 256))
   */
  encodeSolverPasslari(
    encoder: GPUCommandEncoder,
    ortakBindGroup: GPUBindGroup,
    particleBindGroup: GPUBindGroup,
    gridBindGroup: GPUBindGroup,
    solverBindGroup: GPUBindGroup,
    workgroupCount: number,
  ): void;

  /**
   * Whether solver handles its own integration step
   *
   * - true: Solver updates positions internally (PBF)
   * - false: Use common integration pass (DFSPH, PCISPH)
   */
  readonly kendiIntegrasyonuVar: boolean;

  /**
   * Update solver-specific parameters
   *
   * Called each frame before encoding compute passes.
   * Updates solver uniforms (relaxation factors, iteration counts, etc.)
   *
   * @param params - Solver-specific parameter object
   */
  guncelleParametreler(params: any): void;

  /**
   * Clean up all GPU resources
   *
   * Destroys pipelines, buffers, bind groups, shader modules.
   * Called when switching solvers or shutting down simulation.
   */
  temizle(): void;

  /**
   * Get WGSL shader source code
   *
   * Returns the complete shader source for debugging/inspection.
   *
   * @returns WGSL shader code as string
   */
  shaderKaynagi(): string;
}

// ============================================================================
// Solver Factory
// ============================================================================

/**
 * SolverFabrika - Factory for creating SPH solver instances
 *
 * Provides static methods for:
 * - Creating solver instances
 * - Getting solver metadata (descriptions, pass counts, buffer sizes)
 */
export class SolverFabrika {
  /**
   * Create a solver instance by type
   *
   * @param tip - Solver type enum
   * @returns New solver instance
   * @throws Error if solver type not implemented
   */
  static olustur(tip: SolverTipi): ISPHSolver {
    switch (tip) {
      case SolverTipi.DFSPH:
        return new FikoDFSPHSolver();

      case SolverTipi.PBF:
        return new FikoPBFSolver();

      case SolverTipi.PCISPH:
        return new FikoPCISPHSolver();

      default:
        throw new Error(`Unknown solver type: ${tip}`);
    }
  }

  /**
   * Get human-readable solver description
   *
   * @param tip - Solver type
   * @returns Localized description (Turkish)
   */
  static aciklama(tip: SolverTipi): string {
    switch (tip) {
      case SolverTipi.DFSPH:
        return 'DFSPH - Divergence-Free SPH (En yüksek doğruluk)';

      case SolverTipi.PBF:
        return 'PBF - Position-Based Fluids (Hızlı, stabil)';

      case SolverTipi.PCISPH:
        return 'PCISPH - Predictive-Corrective (Dengeli)';

      default:
        return 'Bilinmeyen solver';
    }
  }

  /**
   * Get expected number of compute passes for solver
   *
   * @param tip - Solver type
   * @returns Number of GPU compute passes per frame
   */
  static passSayisi(tip: SolverTipi): number {
    switch (tip) {
      case SolverTipi.DFSPH:
        return 8;  // Divergence solve (2-3 iter) + density solve (2-3 iter) + viscosity + forces

      case SolverTipi.PBF:
        return 6;  // Predict + constraint solve (3-5 iter) + update + viscosity

      case SolverTipi.PCISPH:
        return 7;  // Predict + pressure iteration (3-5 iter) + correct + integrate

      default:
        return 0;
    }
  }

  /**
   * Get extra bytes per particle for solver-specific buffers
   *
   * Used for memory allocation estimates and profiling.
   *
   * @param tip - Solver type
   * @returns Bytes per particle for auxiliary buffers
   */
  static ekstraBytePerParticle(tip: SolverTipi): number {
    switch (tip) {
      case SolverTipi.DFSPH:
        // alpha (f32) + predictedVel (vec4)
        return 4 + 16;  // 20 bytes

      case SolverTipi.PBF:
        // lambda (f32) + deltaPos (vec4) + predictedPos (vec3 + pad)
        return 4 + 16 + 12;  // 32 bytes

      case SolverTipi.PCISPH:
        // pressure (f32) + pressureForce (vec4) + predictedPos (vec3 + pad) + predictedDens (f32)
        return 4 + 16 + 12 + 4;  // 36 bytes

      default:
        return 0;
    }
  }

  /**
   * Get all available solver types
   *
   * @returns Array of all solver type enum values
   */
  static tumTipler(): SolverTipi[] {
    return [
      SolverTipi.DFSPH,
      SolverTipi.PBF,
      SolverTipi.PCISPH,
    ];
  }

  /**
   * Get solver metadata as object
   *
   * @param tip - Solver type
   * @returns Object with solver metadata
   */
  static metadata(tip: SolverTipi): {
    ad: string;
    aciklama: string;
    passSayisi: number;
    ekstraBytes: number;
  } {
    return {
      ad: tip,
      aciklama: this.aciklama(tip),
      passSayisi: this.passSayisi(tip),
      ekstraBytes: this.ekstraBytePerParticle(tip),
    };
  }
}

// ============================================================================
// Common Pipeline Utility Types
// ============================================================================

/**
 * Compute pipeline definition for caching
 *
 * Used to cache compiled pipelines and avoid redundant shader compilation.
 */
export interface ComputePipelineTanimi {
  /** Pipeline name (cache key) */
  ad: string;

  /** Compiled shader module */
  shaderModulu: GPUShaderModule;

  /** Entry point function name in shader */
  entryPoint: string;

  /** Bind group layouts for pipeline */
  bindGroupLayoutlar: GPUBindGroupLayout[];
}

/**
 * Dispatch configuration
 *
 * Bundles pipeline, bind groups, and workgroup counts for a compute pass.
 * Simplifies encoder.dispatchWorkgroups() calls.
 */
export interface DispatchConfig {
  /** Compute pipeline to use */
  pipeline: GPUComputePipeline;

  /** Bind groups to set (indices match array position) */
  bindGroups: GPUBindGroup[];

  /** Number of workgroups in X dimension */
  workgroupCountX: number;

  /** Optional workgroups in Y dimension (default: 1) */
  workgroupCountY?: number;

  /** Optional workgroups in Z dimension (default: 1) */
  workgroupCountZ?: number;

  /** Optional debug label for GPU profiling */
  label?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Execute a dispatch configuration
 *
 * Convenience function to encode a compute pass with all settings.
 *
 * @param encoder - Command encoder or compute pass encoder
 * @param config - Dispatch configuration
 */
export function executeDispatch(
  encoder: GPUCommandEncoder | GPUComputePassEncoder,
  config: DispatchConfig,
): void {
  const pass = 'beginComputePass' in encoder
    ? encoder.beginComputePass({ label: config.label })
    : encoder as GPUComputePassEncoder;

  pass.setPipeline(config.pipeline);

  for (let i = 0; i < config.bindGroups.length; i++) {
    pass.setBindGroup(i, config.bindGroups[i]);
  }

  pass.dispatchWorkgroups(
    config.workgroupCountX,
    config.workgroupCountY ?? 1,
    config.workgroupCountZ ?? 1,
  );

  if ('beginComputePass' in encoder) {
    pass.end();
  }
}

/**
 * Calculate workgroup count for 1D dispatch
 *
 * @param particleCount - Number of particles to process
 * @param workgroupSize - Workgroup size (typically 256)
 * @returns Workgroup count (ceil division)
 */
export function hesaplaWorkgroupSayisi(
  particleCount: number,
  workgroupSize: number = 256,
): number {
  return Math.ceil(particleCount / workgroupSize);
}

/**
 * Validate bind group entries
 *
 * Checks that all buffers exist and sizes are valid.
 *
 * @param entries - Bind group entries to validate
 * @throws Error if validation fails
 */
export function validateBindGroupGirisleri(entries: BindGroupGirisi[]): void {
  for (const entry of entries) {
    if (!entry.buffer) {
      throw new Error(`Bind group entry ${entry.binding} has null buffer`);
    }

    if (entry.offset !== undefined && entry.offset < 0) {
      throw new Error(`Bind group entry ${entry.binding} has negative offset: ${entry.offset}`);
    }

    if (entry.size !== undefined && entry.size <= 0) {
      throw new Error(`Bind group entry ${entry.binding} has invalid size: ${entry.size}`);
    }
  }
}
